import * as Popover from '@radix-ui/react-popover';
import { BLANK, NONE, compareGoals, distinctValues, matchesFilters, orderSprints } from '@mona-roadmap/domain';
import type { Deliverable, FilterContext, FilterState, Metadata, PersonRef, StatusFieldKey } from '@mona-roadmap/domain';
import { ArrowLeft, Check, Filter } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { ColumnDef, ColumnKey, MultiFilterKey } from '../lib/columns';
import { effectiveSelection, filterByText, keepOnlyMatching, matchColumns, toggleValue } from '../lib/filterEditor';
import { Avatar } from './ui/Visuals';

export interface FilterEditorTarget {
  /** Element the popover hangs from and where focus returns afterwards. */
  anchor: HTMLElement;
  /** Column to edit straight away; null starts at the column picker. */
  column: ColumnKey | null;
}

interface Props {
  target: FilterEditorTarget;
  columns: ColumnDef[];
  filters: FilterState;
  onChange: (patch: Partial<FilterState>) => void;
  onClose: () => void;
  deliverables: Deliverable[];
  filterContext: FilterContext;
  metadata: Metadata;
}

/**
 * One keyboard-first filter editor, opened from the Filter button (starts at the column picker) or from a
 * column header / filter chip (starts at that column's values).
 *
 *   F -> type a column -> Enter (top match) -> type a value -> Enter  => keep only matching, close
 *   F -> column -> Enter -> arrows + Space to untick values (all start ticked) -> Enter   => keep what is ticked
 */
export function FilterEditor(props: Props) {
  const { target, onClose } = props;
  const [column, setColumn] = useState<ColumnKey | null>(target.column);
  const def = column ? props.columns.find((c) => c.key === column) : undefined;

  return (
    <Popover.Root open onOpenChange={(open) => !open && onClose()}>
      <Popover.Anchor virtualRef={{ current: target.anchor }} />
      <Popover.Portal>
        <Popover.Content
          className="pop w-[360px]"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          aria-label="Filter"
          data-testid="filter-editor"
          // Focus is handed to the table by the app when the editor closes; don't restore it to the opener.
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {def?.filter ? (
            <ValueEditor {...props} column={def} onBack={() => setColumn(null)} />
          ) : (
            <ColumnPicker columns={props.columns} filters={props.filters} onPick={setColumn} />
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ---- step 1: choose a column -------------------------------------------------------------------------------------

export function isColumnActive(column: ColumnDef, f: FilterState): boolean {
  const k = column.filter;
  if (!k) return false;
  if (k.kind === 'text') return f.name.trim().length > 0;
  if (k.kind === 'date') return !!(f.scopingDueFrom || f.scopingDueTo);
  return f[k.key].length > 0;
}

function ColumnPicker({ columns, filters, onPick }: { columns: ColumnDef[]; filters: FilterState; onPick: (c: ColumnKey) => void }) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const matches = matchColumns(columns, query);
  const clamped = Math.min(active, Math.max(0, matches.length - 1));

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(Math.min(matches.length - 1, clamped + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(Math.max(0, clamped - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const m = matches[clamped];
      if (m) onPick(m.key);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="border-b border-line p-2">
        <div className="mb-1.5 flex items-center gap-1.5 text-faint">
          <Filter className="size-3.5" aria-hidden />
          <span className="field-label">Filter by column</span>
        </div>
        <input
          autoFocus
          className="input"
          role="combobox"
          aria-label="Column to filter"
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={matches.length > 0 ? `${listId}-${clamped}` : undefined}
          placeholder="Type a column, Enter to pick"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      <div id={listId} role="listbox" aria-label="Columns" className="max-h-[300px] overflow-y-auto p-1">
        {matches.length === 0 ? <div className="px-2 py-3 text-center text-faint">No such column</div> : null}
        {matches.map((c, i) => (
          <div
            key={c.key}
            id={`${listId}-${i}`}
            role="option"
            aria-selected={i === clamped}
            data-active={i === clamped}
            className="option"
            onMouseMove={() => setActive(i)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(c.key)}
          >
            <span className="flex-1">{c.label}</span>
            {isColumnActive(c, filters) ? <span className="rounded bg-accent-soft px-1.5 text-[12px] font-medium text-accent-ink">active</span> : null}
          </div>
        ))}
      </div>
      <Hints items={[['↑↓', 'move'], ['Enter', 'pick'], ['Esc', 'close']]} />
    </div>
  );
}

// ---- step 2: edit the column's values ---------------------------------------------------------------------------

function ValueEditor(props: Props & { column: ColumnDef; onBack: () => void }) {
  const { column, filters, onChange, onClose, onBack } = props;
  const kind = column.filter!;
  const title = (
    <div className="flex items-center gap-1 border-b border-line px-2 py-1.5">
      <button type="button" className="btn btn-ghost !h-7 !px-1.5" aria-label="Back to columns" title="Back to columns (Backspace on an empty search)" onClick={onBack}>
        <ArrowLeft className="size-4" aria-hidden />
      </button>
      <span className="flex-1 font-semibold">{column.label}</span>
      <button type="button" className="btn btn-ghost !h-7 !px-2" disabled={!isColumnActive(column, filters)} onClick={() => onChange(clearPatch(kind))}>
        Reset
      </button>
    </div>
  );
  if (kind.kind === 'text') {
    return (
      <div className="flex flex-col">
        {title}
        <div className="flex flex-col gap-2 p-2">
          <label className="field-label" htmlFor="filter-name">
            Name contains
          </label>
          <input
            id="filter-name"
            autoFocus
            className="input"
            placeholder="Type to filter"
            value={filters.name}
            onChange={(e) => onChange({ name: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onClose();
              } else if (e.key === 'Backspace' && filters.name === '') onBack();
            }}
          />
        </div>
        <Hints items={[['Enter', 'apply'], ['Esc', 'close']]} />
      </div>
    );
  }
  if (kind.kind === 'date') {
    const enter = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onClose();
      }
    };
    return (
      <div className="flex flex-col">
        {title}
        <div className="flex flex-col gap-2 p-2" onKeyDown={enter}>
          <div className="field-label">{column.hint ?? column.label} range</div>
          <label className="flex items-center justify-between gap-2 text-dim">
            From
            <input autoFocus type="date" className="input !w-[170px]" aria-label="Due from" value={filters.scopingDueFrom ?? ''} onChange={(e) => onChange({ scopingDueFrom: e.target.value || null })} />
          </label>
          <label className="flex items-center justify-between gap-2 text-dim">
            To
            <input type="date" className="input !w-[170px]" aria-label="Due to" value={filters.scopingDueTo ?? ''} onChange={(e) => onChange({ scopingDueTo: e.target.value || null })} />
          </label>
        </div>
        <Hints items={[['Tab', 'next field'], ['Enter', 'apply'], ['Esc', 'close']]} />
      </div>
    );
  }
  return <MultiValues {...props} filterKey={kind.key} header={title} />;
}

function clearPatch(kind: NonNullable<ColumnDef['filter']>): Partial<FilterState> {
  if (kind.kind === 'text') return { name: '' };
  if (kind.kind === 'date') return { scopingDueFrom: null, scopingDueTo: null };
  return { [kind.key]: [] } as Partial<FilterState>;
}

// ---- multi-select ------------------------------------------------------------------------------------------------

const MULTI_OPTIONS: Partial<Record<MultiFilterKey, StatusFieldKey>> = {
  productAreas: 'productArea',
  types: 'type',
  sprints: 'sprint',
  scopingStatuses: 'scopingStatus',
  statuses: 'status',
};

const PICK: Record<MultiFilterKey, (d: Deliverable, ctx: FilterContext) => Array<{ value: string; label: string }>> = {
  goals: (d, c) => d.productGoalIds.map((id) => ({ value: id, label: c.goalsById.get(id)?.name ?? id })),
  epics: (d, c) => d.epicIds.map((id) => ({ value: id, label: c.epicsById.get(id)?.name ?? id })),
  productAreas: (d) => (d.productArea ? [{ value: d.productArea, label: d.productArea }] : []),
  types: (d) => (d.type ? [{ value: d.type, label: d.type }] : []),
  pmOwners: (d) => d.pmOwners.map((p) => ({ value: p.id, label: p.name })),
  assignees: (d) => d.assignees.map((p) => ({ value: p.id, label: p.name })),
  sprints: (d) => (d.sprint ? [{ value: d.sprint, label: d.sprint }] : []),
  scopingStatuses: (d) => (d.scopingStatus ? [{ value: d.scopingStatus, label: d.scopingStatus }] : []),
  statuses: (d) => (d.status ? [{ value: d.status, label: d.status }] : []),
};

interface Item {
  value: string;
  label: string;
  count: number;
  person?: PersonRef;
}

function useItems(filterKey: MultiFilterKey, filters: FilterState, deliverables: Deliverable[], filterContext: FilterContext, metadata: Metadata): Item[] {
  return useMemo(() => {
    // Counts reflect every OTHER active filter, so you see what choosing a value would yield.
    const without = { ...filters, [filterKey]: [] as string[] };
    const pool = deliverables.filter((d) => matchesFilters(d, without, filterContext));
    const byValue = new Map<string, Item>(distinctValues(pool, (d) => PICK[filterKey](d, filterContext)).map((v) => [v.value, v]));
    const blankLabel = filterKey === 'goals' ? 'No Product Goal' : filterKey === 'epics' ? 'No Epic (standalone)' : '(Blanks)';
    const blank = byValue.get(BLANK);
    if (blank) byValue.set(BLANK, { ...blank, label: blankLabel });
    for (const s of filters[filterKey]) {
      if (s !== NONE && !byValue.has(s)) {
        const person = metadata.people.find((p) => p.id === s);
        const named = filterKey === 'goals' ? filterContext.goalsById.get(s)?.name : filterKey === 'epics' ? filterContext.epicsById.get(s)?.name : undefined;
        byValue.set(s, { value: s, label: s === BLANK ? blankLabel : (named ?? person?.name ?? s), count: 0 });
      }
    }
    const optKey = MULTI_OPTIONS[filterKey];
    let order: string[] | null = null;
    if (filterKey === 'goals') order = [...filterContext.goalsById.values()].sort(compareGoals).map((g) => g.id);
    else if (filterKey === 'epics') order = [...filterContext.epicsById.values()].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)).map((e) => e.id);
    else if (optKey === 'sprint') order = orderSprints(metadata.options.sprint);
    else if (optKey) order = [...metadata.options[optKey]].sort((a, b) => a.index - b.index).map((o) => o.label);
    const rank = (v: string) => (v === BLANK ? Number.MAX_SAFE_INTEGER : order && order.indexOf(v) >= 0 ? order.indexOf(v) : 100_000);
    return [...byValue.values()]
      .map((v) => ({ ...v, person: filterKey === 'pmOwners' || filterKey === 'assignees' ? metadata.people.find((p) => p.id === v.value) : undefined }))
      .sort((a, b) => rank(a.value) - rank(b.value) || a.label.localeCompare(b.label));
  }, [filters, filterKey, deliverables, filterContext, metadata]);
}

function MultiValues({
  column,
  filterKey,
  filters,
  onChange,
  onClose,
  onBack,
  deliverables,
  filterContext,
  metadata,
  header,
}: Props & { column: ColumnDef; filterKey: MultiFilterKey; onBack: () => void; header: React.ReactNode }) {
  const items = useItems(filterKey, filters, deliverables, filterContext, metadata);
  const all = useMemo(() => items.map((i) => i.value), [items]);
  const stored = filters[filterKey];
  const checked = effectiveSelection(stored, all);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const visible = filterByText(items, query);
  const clamped = Math.min(active, Math.max(0, visible.length - 1));
  const set = (values: string[]) => onChange({ [filterKey]: values } as Partial<FilterState>);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${clamped}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [clamped, query]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(Math.min(visible.length - 1, clamped + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(Math.max(0, clamped - 1));
    } else if (e.key === ' ' && query === '') {
      // Space toggles the highlighted value (while the search is empty; otherwise it is part of the text).
      e.preventDefault();
      const v = visible[clamped];
      if (v) set(toggleValue(stored, all, v.value));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Typed something: keep only the matching values. Nothing typed: keep what is ticked.
      if (query.trim() && visible.length > 0) set(keepOnlyMatching(visible.map((v) => v.value), all));
      onClose();
    } else if (e.key === 'Backspace' && query === '') {
      e.preventDefault();
      onBack();
    }
  };

  const allChecked = all.length > 0 && checked.size >= all.length;
  return (
    <div className="flex flex-col">
      {header}
      <div className="border-b border-line p-2">
        <input
          autoFocus
          className="input"
          role="combobox"
          aria-label={`Search ${column.label}`}
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={visible.length > 0 ? `${listId}-${clamped}` : undefined}
          placeholder={`Type to find ${column.label.toLowerCase()}`}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      <div className="flex items-center gap-1 border-b border-line px-2 py-1">
        <button type="button" className="btn btn-ghost !h-7 !px-2" disabled={allChecked} onClick={() => set([])}>
          Select All
        </button>
        <button type="button" className="btn btn-ghost !h-7 !px-2" disabled={checked.size === 0} onClick={() => set([NONE])}>
          Clear Selection
        </button>
        <span className="ml-auto text-[13px] text-faint" data-testid="selected-count">
          {checked.size} of {all.length} selected
        </span>
      </div>
      <div ref={listRef} id={listId} role="listbox" aria-label={column.label} aria-multiselectable className="max-h-[300px] overflow-y-auto p-1">
        {visible.length === 0 ? <div className="px-2 py-3 text-center text-faint">No matches</div> : null}
        {visible.map((item, i) => {
          const on = checked.has(item.value);
          return (
            <div
              key={item.value}
              id={`${listId}-${i}`}
              data-index={i}
              role="option"
              aria-selected={on}
              data-active={i === clamped}
              data-value={item.value}
              className="option"
              onMouseMove={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => set(toggleValue(stored, all, item.value))}
            >
              <span className={`grid size-[18px] flex-none place-items-center rounded border ${on ? 'border-accent bg-accent text-white' : 'border-line-strong'}`} aria-hidden>
                {on ? <Check className="size-3.5" strokeWidth={3} /> : null}
              </span>
              {item.person ? <Avatar person={item.person} /> : null}
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              <span className="text-[13px] text-faint">{item.count}</span>
            </div>
          );
        })}
      </div>
      <Hints items={[['type', 'narrow'], ['Enter', query ? 'keep matches' : 'apply'], ['Space', 'tick'], ['↑↓', 'move'], ['⌫', 'back']]} />
    </div>
  );
}

function Hints({ items }: { items: Array<[string, string]> }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line px-2.5 py-1.5 text-[12.5px] text-faint">
      {items.map(([k, label]) => (
        <span key={k} className="inline-flex items-center gap-1">
          <kbd className="kbd">{k}</kbd> {label}
        </span>
      ))}
    </div>
  );
}
